import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, ArrowLeft, Pencil } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateUtils';
import AddLotModal from './components/AddLotModal';
import EditLotModal from './components/EditLotModal';

export default function LotsAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState<{
    lot_id: string;
    lot_perm_use: string | null;
    lot_zone: string | null;
    lot_serviced: string;
    lot_community: string | null;
    lot_avail: string;
    created_date: string;
  } | null>(null);

  const { data: lots, isLoading } = useQuery({
    queryKey: ['admin', 'lots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('land_lots')
        .select(`
          lot_id,
          lot_perm_use,
          lot_zone,
          lot_serviced,
          lot_community,
          lot_avail,
          created_date,
          lot_community
        `)
        .order('created_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data;
    }
  });

  const filteredLots = lots?.filter(lot => 
    lot.lot_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lot.lot_community?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'lots'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Lots</h1>
            <p className="mt-1 text-sm text-gray-500">
              Add, edit, and manage land lots
            </p>
          </div>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Lot
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search lots..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <Filter className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lot ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Community
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[300px]">
                  Permitted Use
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zone Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredLots?.map((lot) => (
                <tr key={lot.lot_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                    <button 
                      onClick={() => {
                        setSelectedLot(lot);
                        setIsEditModalOpen(true);
                      }}
                      className="text-primary hover:text-primary-dark"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {lot.lot_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lot.lot_community || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-[300px] truncate">
                    {lot.lot_perm_use || 'Not specified'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lot.lot_zone || 'Not specified'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      lot.lot_avail === 'Y'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {lot.lot_avail === 'Y' ? 'Available' : 'Not Available'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(lot.created_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      <AddLotModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {selectedLot && (
        <EditLotModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedLot(null);
          }}
          onSuccess={handleSuccess}
          lot={selectedLot}
        />
      )}
    </div>
  );
}
